import React, { useState } from 'react';
import { Platform, ScrollView, StyleSheet, View, type LayoutChangeEvent } from 'react-native';

type Props = {
  /** Horizontal padding of the block the rail bleeds over (cards start under it). */
  inset: number;
  gap?: number;
  /** Share of the next card left visible: 0.5 = half (owner, 09.10.2026). */
  peek?: number;
  children: React.ReactNode;
};

/** Width used until the rail has measured itself — close to a 360px phone. */
const UNMEASURED_CARD_WIDTH = 220;

/**
 * Mobile-web card rail for the home page: one card in full, the next one
 * peeking in, scroll snaps per card. The card width comes from the rail's own
 * measured width, so page gutters and block paddings never have to be
 * mirrored here. On web `pagingEnabled` is what react-native-web turns into
 * CSS scroll-snap per child; the rail is only rendered on web layouts.
 */
export const HomeCardCarousel: React.FC<Props> = ({ inset, gap = 12, peek = 0.5, children }) => {
  const [railWidth, setRailWidth] = useState(0);
  const onLayout = (event: LayoutChangeEvent) => setRailWidth(Math.round(event.nativeEvent.layout.width));
  // After the leading inset the viewport holds: one card, the gap, `peek` of the next card.
  const cardWidth = railWidth > 0
    ? Math.floor((railWidth - inset - gap) / (1 + peek))
    : UNMEASURED_CARD_WIDTH;
  const items = React.Children.toArray(children);

  return (
    <ScrollView
      horizontal
      pagingEnabled
      decelerationRate="fast"
      showsHorizontalScrollIndicator={false}
      onLayout={onLayout}
      style={[styles.rail, { marginHorizontal: -inset }, Platform.OS === 'web' && ({ scrollPaddingLeft: inset } as any)]}
      contentContainerStyle={[styles.content, { paddingHorizontal: inset, gap }]}
    >
      {items.map((child, index) => (
        <View key={React.isValidElement(child) && child.key != null ? child.key : index} style={{ width: cardWidth }}>
          {child}
        </View>
      ))}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  rail: {
    flexGrow: 0,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
});
